
import { LangChainRouter } from "./langChainRouter";
import { logger } from "@/lib/logger";

export interface AIModelConfig {
  temperature?: number;
  maxTokens?: number;
  model?: string;
}

export interface AIRequestOptions {
  systemPrompt: string;
  userPrompt: string;
  config?: AIModelConfig;
  attachment?: string | null;
}

// Default configurations for different use cases
export const AI_MODEL_CONFIGS = {
  contentGeneration: {
    model: "gpt-4o",
    temperature: 0.8,
    maxTokens: 2000,
  },
  analysis: {
    model: "gpt-4o",
    temperature: 0.2,
    maxTokens: 2000,
  },
  chat: {
    model: "gpt-4o",
    temperature: 0.7,
    maxTokens: 1500,
  },
  classification: {
    model: "gpt-4o-mini",
    temperature: 0.2,
    maxTokens: 500,
  }
};

/**
 * Class for handling AI interactions through LangChain
 */
export class OpenAIClient {
  private langchainRouter: LangChainRouter;
  private _isInitialized = false;

  constructor() {
    this.langchainRouter = new LangChainRouter();
  }

  /**
   * Initialize the client with an API key
   */
  public initialize(apiKey: string): void {
    if (!apiKey) {
      throw new Error("OpenAI API key is required");
    }

    this.langchainRouter.initialize(apiKey);
    this._isInitialized = true;
    logger.info({ message: "AI client initialized successfully with LangChain" });
  }

  /**
   * Check if the client is initialized
   */
  get isInitialized(): boolean {
    return this._isInitialized;
  }

  /**
   * Generate a response using LangChain
   */
  public async generate(options: AIRequestOptions): Promise<string> {
    if (!this._isInitialized) {
      throw new Error("AI client not initialized. Call initialize() first.");
    }

    try {
      return await this.langchainRouter.generateResponse(options);
    } catch (error) {
      logger.error({
        message: "Error generating AI response",
        error: error instanceof Error ? error : new Error(String(error)),
        context: { ...options },
      });
      throw error;
    }
  }
}

// Create a singleton instance
const openAIClient = new OpenAIClient();
export default openAIClient;
